/**
 * KvkkCompany Integration
 */
import i18next from 'i18next';
import { toast } from 'sonner';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';
import { useKvkkCompanyStore } from '@/store/kvkk-company.store.js';
import { Logger } from '@/lib/app-log.js';

export class KvkkCompanyIntegration {
    constructor(sdkService, userService) {
        this.service = sdkService;
        this.userService = userService;
    }

    async fetchAll(params = {}) {
        try {
            const response = await this.service.getAll(params);
            return { success: true, data: response.data };
        } catch (error) {
            toast.error(getIntegrationErrorMessage(error));
            return { success: false, error: getIntegrationErrorMessage(error) };
        }
    }

    async getById(id) {
        try {
            const response = await this.service.getById(id);
            return { success: true, data: response.data };
        } catch (error) {
            toast.error(getIntegrationErrorMessage(error));
            return { success: false, error: getIntegrationErrorMessage(error) };
        }
    }

    async getCompanyInfo() {
        const store = useKvkkCompanyStore.getState();
        store.setLoading(true);
        store.setError(null);
        try {
            const response = await this.service.getAll();
            const company = response.data && response.data.length > 0 ? response.data[0] : null;

            if (company) {
                store.setCompanyData(company);
            }

            return { success: true, data: company };
        } catch (error) {
            toast.error(getIntegrationErrorMessage(error));
            store.setError(getIntegrationErrorMessage(error));
            return { success: false, error: getIntegrationErrorMessage(error) };
        } finally {
            store.setLoading(false);
        }
    }

    async fetchUsers(params = {}) {
        try {
            const response = await this.userService.getAll({
                pageSize: params.pageSize || 100,
                page: params.page || 1,
            });
            useKvkkCompanyStore.getState().setUsers(response.data || []);
            return { success: true, data: response.data || [] };
        } catch (error) {
            Logger.error('Error fetching users:', { error: error?.message });
            return { success: false, error: getIntegrationErrorMessage(error), data: [] };
        }
    }

    async saveCompanyData(data) {
        const store = useKvkkCompanyStore.getState();
        const { companyData } = store;
        store.setSaving(true);
        store.setError(null);

        try {
            let result;
            if (companyData && (companyData._id || companyData.id)) {
                const id = companyData._id || companyData.id;
                result = await this.update(id, data);
            } else {
                result = await this.create(data);
            }
            return result;
        } catch (error) {
            store.setError(getIntegrationErrorMessage(error));
            return { success: false, error: getIntegrationErrorMessage(error) };
        } finally {
            store.setSaving(false);
        }
    }

    async create(data) {
        try {
            const response = await this.service.create(data);
            useKvkkCompanyStore.getState().setCompanyData(response.data);
            toast.success(i18next.t('integrationToasts:companyInformationSaved', { defaultValue: 'Company information saved' }));
            return { success: true, data: response.data };
        } catch (error) {
            toast.error(getIntegrationErrorMessage(error));
            return { success: false, error: getIntegrationErrorMessage(error) };
        }
    }

    async update(id, data) {
        try {
            const response = await this.service.update(id, data);
            useKvkkCompanyStore.getState().setCompanyData(response.data);
            toast.success(i18next.t('integrationToasts:companyInformationUpdated', { defaultValue: 'Company information updated' }));
            return { success: true, data: response.data };
        } catch (error) {
            toast.error(getIntegrationErrorMessage(error));
            return { success: false, error: getIntegrationErrorMessage(error) };
        }
    }

    async delete(id) {
        try {
            await this.service.delete(id);
            toast.success(i18next.t('integrationToasts:deletedSuccessfully', { defaultValue: 'Deleted successfully' }));
            return { success: true };
        } catch (error) {
            toast.error(getIntegrationErrorMessage(error));
            return { success: false, error: getIntegrationErrorMessage(error) };
        }
    }
}
